/**
 * @file index.tsx
 * @module app/modules/activity-logs/index
 * @description Unified Activity Logs report reading directly from the backend `audit_logs`
 * collection (`_auditLog/admin/logs`), replacing the 30 legacy per-entity logger-mechanism pages.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from 'react';
import { Card, Col, Modal, Row } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import Loader from '../../../Global/loader';
import Pagination from '../../../Global/pagination';
import Method from '../../../utils/methods';
import { error } from '../../../Global/toast';
import { CustomSelectWhite } from '../../custom/Select/CustomSelectWhite';
import { PAGE_LIMIT_MID } from '../../../utils/constants';
import { usePagination } from '../_common/hooks/table-pagination';
import {
  fetchActivityLogFilters,
  fetchActivityLogs,
} from './services/activity-log.service';
import {
  IActivityLog,
  IActivityLogFilterOption,
} from './types/activity-log.types';

const UTRADE_PLATFORM_VALUE = 2;

/**
 * Unified Activity Logs report reading directly from the backend `audit_logs`
 * collection (`_auditLog/admin/logs`), replacing the 30 legacy per-entity logger-mechanism pages.
 */
const ActivityLogs = () => {
  const [fetchLoading, setFetchLoading] = useState(false);
  const [logs, setLogs] = useState<IActivityLog[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [platformTypes, setPlatformTypes] = useState<IActivityLogFilterOption[]>([]);
  const [actionTypes, setActionTypes] = useState<IActivityLogFilterOption[]>([]);
  const [moduleTypes, setModuleTypes] = useState<IActivityLogFilterOption[]>([]);

  const [selectedPlatform, setSelectedPlatform] = useState<any>(null);
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [selectedAction, setSelectedAction] = useState<any>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [selectedLog, setSelectedLog] = useState<IActivityLog | null>(null);

  /**
   * Fetches paginated `audit_logs` records for the unified Activity Logs report.
   */
  const fetchData = async (
    pageNo: number,
    limit: number,
    platform: any,
    moduleType: any,
    action: any,
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setFetchLoading(true);
    try {
      const response: any = await fetchActivityLogs({
        pageNo,
        limit,
        platform: platform?.value,
        type: moduleType?.value,
        logType: action?.value,
        dateFrom: fromDate
          ? Method.convertDateToFormat(fromDate, 'yyyy-MM-DD')
          : undefined,
        dateTo: toDate
          ? Method.convertDateToFormat(toDate, 'yyyy-MM-DD')
          : undefined,
      });
      if (response?.records) {
        setLogs(response.records);
        setTotalRecords(response.total || 0);
      } else {
        setLogs([]);
        setTotalRecords(0);
      }
    } catch (err) {
      error('Error fetching activity logs');
    }
    setFetchLoading(false);
  };

  const {
    page,
    pageLimit,
    setPage,
    handleCurrentPage,
    handleNextPage,
    handlePreviousPage,
    handlePageLimit,
  } = usePagination(
    (pageNo, limit) =>
      fetchData(
        pageNo,
        limit,
        selectedPlatform,
        selectedModule,
        selectedAction,
        dateFrom,
        dateTo
      ),
    PAGE_LIMIT_MID
  );

  /**
   * Fetches platform/action dropdown options from the backend, mirroring
   * GlobalPlatformProjectTypes/GlobalPlatformLogsTypes (utrade-backend CommonConstants.js)
   * so the frontend never has to hardcode/duplicate these enums.
   */
  useEffect(() => {
    fetchActivityLogFilters()
      .then((response: any) => {
        const fetchedPlatformTypes = response?.platformTypes || [];
        const fetchedActionTypes = response?.actionTypes || [];
        const fetchedModuleTypes = response?.moduleTypes || [];
        setPlatformTypes(fetchedPlatformTypes);
        setActionTypes(fetchedActionTypes);
        setModuleTypes(fetchedModuleTypes);
        setSelectedPlatform(
          fetchedPlatformTypes.find(
            (item: IActivityLogFilterOption) => item.value === UTRADE_PLATFORM_VALUE
          ) || null
        );
      })
      .catch(() => error('Error fetching activity log filters'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetches paginated `audit_logs` records for the unified Activity Logs report.
   * Triggered whenever any of the filter values change.
   */
  useEffect(() => {
    fetchData(
      page,
      pageLimit,
      selectedPlatform,
      selectedModule,
      selectedAction,
      dateFrom,
      dateTo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, selectedModule, selectedAction, dateFrom, dateTo]);

  return (
    <div className="p-5">
      <Row className="">
        <Col md={3}>
          <CustomSelectWhite
            options={platformTypes}
            value={selectedPlatform}
            onChange={(event: any) => {
              setPage(1);
              setSelectedPlatform(event);
            }}
            isClearable
            placeholder="Platform"
          />
        </Col>
        <Col md={3}>
          <CustomSelectWhite
            options={moduleTypes}
            value={selectedModule}
            onChange={(event: any) => {
              setPage(1);
              setSelectedModule(event);
            }}
            isClearable
            placeholder="Type"
          />
        </Col>
        <Col md={2}>
          <CustomSelectWhite
            options={actionTypes}
            value={selectedAction}
            onChange={(event: any) => {
              setPage(1);
              setSelectedAction(event);
            }}
            isClearable
            placeholder="Action"
          />
        </Col>
        <Col md={2}>
          <ReactDatePicker
            className="form-control bg-white min-h-50px fs-16 fw-bold text-dark"
            selected={dateFrom}
            onChange={(date: any) => {
              setPage(1);
              setDateFrom(date);
            }}
            placeholderText="From"
            dateFormat="dd/MM/yyyy"
            isClearable
          />
        </Col>
        <Col md={2}>
          <ReactDatePicker
            className="form-control bg-white min-h-50px fs-16 fw-bold text-dark"
            selected={dateTo}
            onChange={(date: any) => {
              setPage(1);
              setDateTo(date);
            }}
            placeholderText="To"
            dateFormat="dd/MM/yyyy"
            isClearable
          />
        </Col>
      </Row>
      <Card className="border border-r10px mt-6">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
              <thead>
                <tr className="fs-16 fw-600 align-middle">
                  <th className="col-w-15">Date</th>
                  <th className="col-w-10">Platform</th>
                  <th className="col-w-15">Type</th>
                  <th className="col-w-15">Action</th>
                  <th className="col-w-15">User</th>
                  <th className="col-w-30">Message</th>
                </tr>
              </thead>
              <tbody className="fs-15 fw-600">
                {fetchLoading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="d-flex justify-content-center">
                        <Loader loading={fetchLoading} />
                      </div>
                    </td>
                  </tr>
                ) : logs.length ? (
                  logs.map((log) => (
                    <tr
                      key={log._id}
                      role="button"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td>
                        {log._createdAt
                          ? Method.convertDateToDDMMYYYYHHMM(log._createdAt)
                          : '-'}
                      </td>
                      <td>
                        {platformTypes.find(
                          (item) => item.value === log.platform
                        )?.label || '-'}
                      </td>
                      <td>
                        {moduleTypes.find(
                          (item) => item.value === log.type
                        )?.label ||
                          log.type ||
                          '-'}
                      </td>
                      <td>
                        {actionTypes.find(
                          (item) => item.value === log.logType
                        )?.label || '-'}
                      </td>
                      <td>{log.user?.name || '-'}</td>
                      <td
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: 400 }}
                      >
                        {log.message || '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                        No Data found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      {!fetchLoading ? (
        <Pagination
          totalRecords={totalRecords}
          currentPage={page}
          handleCurrentPage={handleCurrentPage}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
          handlePageLimit={handlePageLimit}
          pageLimit={pageLimit}
        />
      ) : (
        <></>
      )}
      <Modal size="lg" show={!!selectedLog} onHide={() => setSelectedLog(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fs-15 fw-600">{selectedLog?.message || '-'}</p>
          {selectedLog?.data?.length ? (
            <pre className="bg-light p-3 rounded fs-13">
              {JSON.stringify(selectedLog.data, null, 2)}
            </pre>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ActivityLogs;
